import {alpha, PaletteColor, PaletteColorOptions} from "@mui/material/styles";

// ----------------------------------------------------------------------

export type ColorSchema = "primary" | "secondary" | "info" | "success" | "warning" | "error";

declare module "@mui/material/styles/createPalette" {
    interface TypeBackground {
        neutral: string;
    }

    interface SimplePaletteColorOptions {
        lighter: string;
        darker: string;
    }

    interface PaletteColor {
        lighter: string;
        darker: string;
    }
}

type CustomPaletteColors =
    | "eliga_42C"
    | "eliga_BD7"
    | "eliga_0AE"
    | "eliga_168"
    | "eliga_8FC"
    | "eliga_4DE"
    | "eliga_E3FC"
    | "eliga_B3FC"
    | "eliga_0F5"
    | "eliga_2EC"
    | "eliga_2FF"
    | "eliga_4F5"
    | "eliga_E28"
    | "eliga_934"
    | "eliga_B4C"
    | "eliga_75F"
    | "eliga_680"
    | "eliga_399"
    | "eliga_6B6"
    | "eliga_EDA"
    | "eliga_6ED"
    | "eliga_6FA"
    | "eliga_CFE"
    | "eliga_AF9"
    | "eliga_BFC"
    | "eliga_4FE"
    | "eliga_7FF"
    | "eliga_B5B"
    | "eliga_BDB"
    | "eliga_BEB"
    | "eliga_7F7"
    | "eliga_E56"
    | "eliga_83B"
    | "eliga_38A"
    | "eliga_242"
    | "eliga_8B9"
    | "eliga_6F0"
    | "eliga_FFE"
    | "eliga_FFF"
    | "eliga_3FF"
    | "eliga_E70"
    | "eliga_272"
    | "eliga_74C"
    | "eliga_CCC";

type CustomPalette = {
    [_ in CustomPaletteColors]: PaletteColor;
};
type CustomPaletteOptions = {
    [_ in keyof CustomPalette]?: PaletteColorOptions;
};
type CustomButtonColorOptions = {
    [_ in CustomPaletteColors]?: true;
};

declare module "@mui/material/styles" {
    interface Palette extends CustomPalette {
    }

    interface PaletteOptions extends CustomPaletteOptions {
    }
}

declare module "@mui/material" {
    interface ButtonPropsColorOverrides extends CustomButtonColorOptions {
    }
}

declare module '@mui/material/Chip' {
    interface ChipPropsColorOverrides extends CustomButtonColorOptions {
    }
}

// SETUP COLORS

// export const grey = {
//   0: '#FFFFFF',
//   100: '#f1f1f1',
//   200: '#eee',
//   300: '#ccc',
//   400: '#aaa',
//   500: '#999',
//   600: '#777',
//   700: '#555',
//   800: '#333',
//   900: '#111',
// };

export const grey = {
    0: "#FFFFFF",
    100: "#FEFEFE",
    200: "#FAF6FA",
    300: "#F9F9F9",
    400: "#EEE",
    500: "#777777",
    600: "#9FA6B6",
    700: "#AEAEAE",
    800: "#0A142C",
    900: "#000",
};

export const primary = {
    lighter: "#C8FAD6",
    light: "#5BE49B",
    main: "#0A142C",
    dark: "#007867",
    darker: "#004B50",
    contrastText: "#FFFFFF",
};

export const secondary = {
    lighter: "#EFD6FF",
    light: "#C684FF",
    main: "#8E33FF",
    dark: "#5119B7",
    darker: "#27097A",
    contrastText: "#FFFFFF",
};

export const info = {
    lighter: "#CAFDF5",
    light: "#61F3F3",
    main: "#00B8D9",
    dark: "#006C9C",
    darker: "#003768",
    contrastText: "#FFFFFF",
};

export const success = {
    lighter: "#D3FCD2",
    light: "#77ED8B",
    main: "#22C55E",
    dark: "#118D57",
    darker: "#065E49",
    contrastText: "#ffffff",
};

export const warning = {
    lighter: "#FFF5CC",
    light: "#FFD666",
    main: "#FFAB00",
    dark: "#B76E00",
    darker: "#7A4100",
    contrastText: grey[800],
};

export const error = {
    lighter: "#FFE9D5",
    light: "#FFAC82",
    main: "#FF5630",
    dark: "#B71D18",
    darker: "#7A0916",
    contrastText: "#FFFFFF",
};

export const common = {
    black: "#000000",
    white: "#FFFFFF",
};

export const eliga_222 = {
    light: "#222222",
    main: "#222222",
};

export const eliga_666 = {
    light: "#666666",
    main: "#666666",
};

export const eliga_42C = {
    light: "#0A142C",
    main: "#0A142C",
};

export const eliga_E28 = {
    light: "#1A1E28",
    main: "#1A1E28",
};

export const eliga_934 = {
    light: "#252934",
    main: "#252934",
};

export const eliga_B4C = {
    light: "#343B4C",
    main: "#343B4C",
};

export const eliga_75F = {
    light: "#3D475F",
    main: "#3D475F",
};

export const eliga_168 = {
    light: "#475168",
    main: "#475168",
};

export const eliga_680 = {
    light: "#5A6680",
    main: "#5A6680",
};

export const eliga_399 = {
    light: "#798399",
    main: "#798399",
};

export const eliga_6B6 = {
    light: "#9FA6B6",
    main: "#9FA6B6",
};

export const eliga_EDA = {
    light: "#C8CEDA",
    main: "#C8CEDA",
};

export const eliga_6ED = {
    light: "#E3E6ED",
    main: "#E3E6ED",
    contrastText: "#5A6680",
};

export const eliga_0F5 = {
    light: "#EEF0F5",
    main: "#EEF0F5",
    contrastText: "#5A6680",
};

export const eliga_6FA = {
    light: "#F4F6FA",
    main: "#F4F6FA",
    dark: "#EEF0F5",
    contrastText: "#5A6680",
};

export const eliga_CFE = {
    light: "#FAFCFE",
    main: "#FAFCFE",
};

export const eliga_AF9 = {
    light: "#259AF9",
    main: "#259AF9",
};

export const eliga_B3FC = {
    light: "#59B3FC",
    main: "#59B3FC",
};

export const eliga_BFC = {
    light: "#B1DBFC",
    main: "#B1DBFC",
};

export const eliga_8FC = {
    light: "#CFE8FC",
    main: "#CFE8FC",
};

export const eliga_4FE = {
    light: "#E9F4FE",
    main: "#E9F4FE",
    contrastText: "#259AF9",
};

export const eliga_2EC = {
    light: "#2682EC",
    main: "#2682EC",
    contrastText: "#FFFFFF",
};

export const eliga_E3FC = {
    light: "#D2E3FC",
    main: "#D2E3FC",
    contrastText: "#2682EC",
};

export const eliga_2FF = {
    light: "#E9F2FF",
    main: "#E9F2FF",
    contrastText: "#2682EC",
};

export const eliga_7FF = {
    light: "#F2F7FF",
    main: "#F2F7FF",
    contrastText: "#2682EC",
};

export const eliga_B5B = {
    light: "#FF5B5B",
    main: "#FF5B5B",
};

export const eliga_BDB = {
    light: "#FFDBDB",
    main: "#FFDBDB",
    contrastText: "#FF5B5B",
};

export const eliga_BEB = {
    light: "#FFEBEB",
    main: "#FFEBEB",
    contrastText: "#FF5B5B",
};

export const eliga_7F7 = {
    light: "#FFF7F7",
    main: "#FFF7F7",
    contrastText: "#FF5B5B",
};

export const eliga_E56 = {
    light: "#61DE56",
    main: "#61DE56",
};

export const eliga_BD7 = {
    light: "#D9FBD7",
    main: "#D9FBD7",
    contrastText: "#065E49",
};

export const eliga_83B = {
    light: "#FFC83B",
    main: "#FFC83B",
};

export const eliga_38A = {
    light: "#00938A",
    main: "#00938A",
};

export const eliga_242 = {
    light: "#FF9242",
    main: "#FF9242",
};

export const eliga_4DE = {
    light: "#5674DE",
    main: "#5674DE",
};

export const eliga_8B9 = {
    light: "#BDF8B9",
    main: "#BDF8B9",
};

export const eliga_0AE = {
    light: "#F9E0AE",
    main: "#F9E0AE",
};

export const eliga_4F5 = {
    light: "#CCD4F5",
    main: "#CCD4F5",
};

export const eliga_6F0 = {
    light: "#FFF6F0",
    main: "#FFF6F0",
};

export const eliga_FFE = {
    light: "#F0FFFE",
    main: "#F0FFFE",
};

export const eliga_FFF = {
    light: "#FFFFFF",
    main: "#FFFFFF",
    dark: "#DDDDDD",
    contrastText: "#777",
};

export const eliga_3FF = {
    light: "#F2F7FF",
    main: "#59C3FF",
    contrastText: "#FFFFFF"
}

export const eliga_E70 = {
    light: "#F2F7FF",
    main: "#5BDE70",
    contrastText: "#FFFFFF"
}

export const eliga_272 = {
    light: "#F2F7FF",
    main: "#FF7272",
    contrastText: "#FFFFFF"
}

export const eliga_74C = {
    light: "#F2F7FF",
    main: "#FF974C",
    contrastText: "#FFFFFF"
}

export const eliga_CCC = {
    light: "#CCCCCC",
    main: "#CCCCCC",
}

export const action = {
    hover: alpha(grey[500], 0.08),
    selected: alpha(grey[500], 0.8),
    disabled: alpha(grey[500], 0.16),
    disabledBackground: alpha(eliga_6FA.main, 1),
    focus: alpha(grey[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
};

const base = {
    primary,
    secondary,
    info,
    success,
    warning,
    error,
    grey,
    common,
    divider: "#EEF0F5",
    action,
    eliga_222,
    eliga_666,
    eliga_42C,
    eliga_BD7,
    eliga_0AE,
    eliga_168,
    eliga_8FC,
    eliga_4DE,
    eliga_E3FC,
    eliga_B3FC,
    eliga_0F5,
    eliga_2EC,
    eliga_2FF,
    eliga_4F5,
    eliga_E28,
    eliga_934,
    eliga_B4C,
    eliga_75F,
    eliga_680,
    eliga_399,
    eliga_6B6,
    eliga_EDA,
    eliga_6ED,
    eliga_6FA,
    eliga_CFE,
    eliga_AF9,
    eliga_BFC,
    eliga_4FE,
    eliga_7FF,
    eliga_B5B,
    eliga_BDB,
    eliga_BEB,
    eliga_7F7,
    eliga_E56,
    eliga_83B,
    eliga_38A,
    eliga_242,
    eliga_8B9,
    eliga_6F0,
    eliga_FFE,
    eliga_FFF,
    eliga_3FF,
    eliga_E70,
    eliga_272,
    eliga_74C,
    eliga_CCC
};

// ----------------------------------------------------------------------

export function palette(mode: "light" | "dark") {
    const light = {
        ...base,
        mode: mode,
        text: {
            primary: grey[800],
            secondary: eliga_168.main,
            disabled: grey[600],
        },
        background: {
            paper: "#FFFFFF",
            default: "#FFFFFF",
            neutral: grey[200],
        },
        action: {
            ...base.action,
            active: grey[600],
        },
    };

    const dark = {
        ...base,
        mode: "dark",
        text: {
            primary: "#FFFFFF",
            secondary: grey[500],
            disabled: grey[600],
        },
        background: {
            paper: grey[800],
            default: grey[900],
            neutral: alpha(grey[500], 0.12),
        },
        action: {
            ...base.action,
            active: grey[500],
        },
    };

    return mode === "light" ? light : dark;
}
